import { isAxiosError } from 'axios';
import { captureSentryException, captureSentryMessage } from '@/lib/sentry';

type FrontendErrorSource =
  'report_error' | 'window_error' | 'unhandled_rejection' | 'error_boundary';
type FrontendErrorSeverity = 'error' | 'warning';

type RemoteErrorContext = Record<string, unknown> & {
  source?: FrontendErrorSource;
  severity?: FrontendErrorSeverity;
  skipRemoteReport?: boolean;
};

const browserExtensionNoisePatterns = [
  'runtime.lasterror',
  'could not establish connection',
  'message port closed',
  'receiving end does not exist',
  'extension context invalidated',
  'chain is not set up',
  'initializing provider',
] as const;

const browserExtensionSourcePatterns = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'ms-browser-extension://',
  'injected.bundle.js',
] as const;

// Note: we intentionally resolve `console.error` at *call* time (instead of
// capturing a bound reference at module load) so test suites that spy on
// `console.error` observe the calls. See `__testing.resetRuntimeState` — the
// only state that needs explicit reset is the dedup counters below.
const consoleError = (...args: Parameters<Console['error']>) => console.error(...args);
const frontendErrorEndpoint = '/api/public/frontend-errors';
const maxRemoteReportsPerPage = 20;
const maxDuplicateReportsPerSignature = 3;
const remoteReportCounts = new Map<string, number>();

let remoteReportsSent = 0;
let globalErrorHandlersRegistered = false;
let remoteReportingFailureLogged = false;
const chunkReloadStorageKey = 'ru24_chunk_reload_attempted';

function safeToString(value: unknown): string {
  try {
    return value != null ? String(value) : '';
  } catch {
    return '';
  }
}

function getCandidateStrings(value: unknown): string[] {
  if (value == null) {
    return [];
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [safeToString(value)];
  }

  if (value instanceof Error) {
    return [value.message, value.stack ?? ''].filter(Boolean);
  }

  if (typeof value === 'object') {
    const candidateKeys = [
      'message',
      'stack',
      'filename',
      'fileName',
      'sourceURL',
      'sourceUrl',
      'scriptUrl',
      'name',
    ] as const;

    const candidates = candidateKeys
      .map((key) => Reflect.get(value, key))
      .filter((candidate) => candidate != null)
      .map((candidate) => safeToString(candidate))
      .filter(Boolean);

    if ('reason' in value) {
      candidates.push(...getCandidateStrings((value as { reason?: unknown }).reason));
    }

    return candidates;
  }

  return [safeToString(value)].filter(Boolean);
}

export function isBrowserExtensionNoise(value: unknown): boolean {
  const normalizedCandidates = getCandidateStrings(value)
    .map((candidate) => candidate.trim().toLowerCase())
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return false;
  }

  return normalizedCandidates.some(
    (candidate) =>
      browserExtensionNoisePatterns.some((pattern) => candidate.includes(pattern)) ||
      browserExtensionSourcePatterns.some((pattern) => candidate.includes(pattern))
  );
}

function containsBrowserExtensionNoise(values: unknown[]): boolean {
  return values.some((value) => {
    if (isBrowserExtensionNoise(value)) {
      return true;
    }

    if (value && typeof value === 'object' && 'message' in value) {
      return isBrowserExtensionNoise((value as { message?: unknown }).message);
    }

    return false;
  });
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }

  if (isAxiosError<{ error?: string; message?: string }>(error)) {
    const apiError = error.response?.data;

    if (typeof apiError?.error === 'string' && apiError.error.trim().length > 0) {
      return apiError.error;
    }

    if (typeof apiError?.message === 'string' && apiError.message.trim().length > 0) {
      return apiError.message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

export function hasHttpStatus(error: unknown, status: number): boolean {
  return isAxiosError(error) && error.response?.status === status;
}

function getCurrentRoute(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Query strings and hashes can contain reset/payment tokens or user-entered
  // data. The pathname is sufficient to reproduce and group a frontend error.
  return window.location.pathname || null;
}

function getResourceDescription(target: EventTarget | null): {
  resourceType: string;
  resourcePath: string | null;
} | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const resourceType = target.tagName.toLowerCase();
  const rawUrl =
    target.getAttribute('src') ?? target.getAttribute('href') ?? target.getAttribute('data-src');

  if (!rawUrl) {
    return { resourceType, resourcePath: null };
  }

  try {
    const url = new URL(rawUrl, window.location.href);
    return { resourceType, resourcePath: url.pathname.slice(0, 500) };
  } catch {
    return { resourceType, resourcePath: rawUrl.split(/[?#]/, 1)[0].slice(0, 500) };
  }
}

function getErrorStack(error: unknown): string | null {
  if (error instanceof Error && typeof error.stack === 'string' && error.stack.trim().length > 0) {
    return error.stack.slice(0, 10_000);
  }

  return null;
}

function normalizeContextValue(value: unknown): string | number | boolean | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    return value.slice(0, 500);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return value.message.slice(0, 500);
  }

  try {
    return JSON.stringify(value).slice(0, 500);
  } catch {
    return safeToString(value).slice(0, 500) || null;
  }
}

function sanitizeContext(
  context?: RemoteErrorContext
): Record<string, string | number | boolean | null> | undefined {
  if (!context) {
    return undefined;
  }

  const entries = Object.entries(context)
    .filter(([key]) => key !== 'source' && key !== 'severity' && key !== 'skipRemoteReport')
    .slice(0, 20)
    .map(
      ([key, value]) =>
        [
          key,
          /(password|passphrase|token|secret|authorization|cookie|api[-_]?key)/i.test(key)
            ? '[REDACTED]'
            : normalizeContextValue(value),
        ] as const
    );

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries);
}

function sendFrontendErrorReport(
  message: string,
  error: unknown,
  context?: RemoteErrorContext
): void {
  if (!import.meta.env.PROD || typeof window === 'undefined' || context?.skipRemoteReport) {
    return;
  }

  if (remoteReportsSent >= maxRemoteReportsPerPage) {
    return;
  }

  const source = context?.source ?? 'report_error';
  const severity = context?.severity ?? 'error';
  const payload = {
    message,
    source,
    severity,
    path: getCurrentRoute(),
    stack: getErrorStack(error),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    context: sanitizeContext(context),
  };

  const signature = `${source}|${severity}|${payload.message}|${payload.path ?? ''}`;
  const duplicateCount = remoteReportCounts.get(signature) ?? 0;
  if (duplicateCount >= maxDuplicateReportsPerSignature) {
    return;
  }

  remoteReportCounts.set(signature, duplicateCount + 1);
  remoteReportsSent += 1;

  const body = JSON.stringify(payload);
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const beaconAccepted = navigator.sendBeacon(
      frontendErrorEndpoint,
      new Blob([body], { type: 'application/json' })
    );
    if (beaconAccepted) {
      return;
    }
  }

  void fetch(frontendErrorEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    keepalive: true,
  }).catch((reportingError) => {
    if (!remoteReportingFailureLogged) {
      remoteReportingFailureLogged = true;
      consoleError('Frontend error reporting failed', reportingError);
    }
  });
}

function buildSentryContext(context?: RemoteErrorContext): Record<string, unknown> | undefined {
  if (!context) {
    return undefined;
  }
  const sanitized = sanitizeContext(context);
  const { source, severity } = context;
  const meta = {
    ...(source ? { source } : {}),
    ...(severity ? { severity } : {}),
  };
  if (!sanitized && Object.keys(meta).length === 0) {
    return undefined;
  }
  return { ...sanitized, ...meta };
}

export function reportError(message: string, error?: unknown, context?: RemoteErrorContext): void {
  const payload = [message, error, context].filter((value) => value !== undefined);

  if (containsBrowserExtensionNoise(payload)) {
    return;
  }

  consoleError(...payload);
  sendFrontendErrorReport(message, error, context);

  if (error !== undefined && error !== null) {
    captureSentryException(error, buildSentryContext(context));
  } else {
    captureSentryMessage(message, buildSentryContext(context));
  }
}

export function registerGlobalErrorHandlers(): void {
  if (globalErrorHandlersRegistered || typeof window === 'undefined') {
    return;
  }

  globalErrorHandlersRegistered = true;

  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    reportError('Frontend chunk loading failed', payload, {
      source: 'window_error',
      chunkRecovery: true,
    });

    // Deployments replace hashed chunks while users can still have an older
    // page open. Reload once to fetch the current HTML/chunk map, but prevent
    // loops when the actual cause is an offline connection or CDN outage.
    try {
      if (window.sessionStorage.getItem(chunkReloadStorageKey) !== '1') {
        window.sessionStorage.setItem(chunkReloadStorageKey, '1');
        event.preventDefault();
        window.location.reload();
      }
    } catch {
      // sessionStorage can be disabled; the normal ErrorBoundary remains active.
    }
  });

  window.addEventListener('pageshow', () => {
    try {
      window.sessionStorage.removeItem(chunkReloadStorageKey);
    } catch {
      // Ignore unavailable browser storage.
    }
  });

  window.addEventListener(
    'error',
    (event) => {
      const errorEvent = event instanceof ErrorEvent ? event : null;
      const resource = getResourceDescription(event.target);

      if (resource && !errorEvent?.error && !errorEvent?.message) {
        const error = new Error(
          `Failed to load ${resource.resourceType}${resource.resourcePath ? `: ${resource.resourcePath}` : ''}`
        );
        reportError('Frontend resource loading failed', error, {
          source: 'window_error',
          resourceType: resource.resourceType,
          resourcePath: resource.resourcePath,
        });
        return;
      }

      if (
        isBrowserExtensionNoise(event) ||
        isBrowserExtensionNoise(errorEvent?.error) ||
        isBrowserExtensionNoise(errorEvent?.filename)
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      reportError('Unhandled window error', errorEvent?.error ?? errorEvent?.message ?? event, {
        source: 'window_error',
        fileName: errorEvent?.filename || null,
        lineNumber: errorEvent?.lineno || null,
        columnNumber: errorEvent?.colno || null,
      });
    },
    true
  );

  window.addEventListener('unhandledrejection', (event) => {
    if (isBrowserExtensionNoise(event) || isBrowserExtensionNoise(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    reportError('Unhandled promise rejection', event.reason, {
      source: 'unhandled_rejection',
    });
  });
}

export const __testing = {
  safeToString,
  getCandidateStrings,
  containsBrowserExtensionNoise,
  getCurrentRoute,
  getResourceDescription,
  sendFrontendErrorReport,
  getErrorStack,
  normalizeContextValue,
  sanitizeContext,
  buildSentryContext,
  resetRuntimeState: () => {
    remoteReportsSent = 0;
    remoteReportCounts.clear();
    remoteReportingFailureLogged = false;
    globalErrorHandlersRegistered = false;
  },
};
